import { MessagePrimitive, MessageProvider, useAui } from '@assistant-ui/react';
import { MarkdownTextPrimitive } from '@assistant-ui/react-markdown';
import { ChatMarkdownLink, getChatMarkdownStyles } from 'components/Chat/ChatMarkdown';
import ChatPromptDispatcher from 'components/Chat/ChatPromptDispatcher';
import useChatMessagesViewModel from 'hooks/chat/useChatMessagesViewModel';
import { Trans, useTranslation } from 'react-i18next';
import remarkGfm from 'remark-gfm';
import { IconButton, Stack, styled, Tag, Text } from 'shell/components';
import { ChatRole } from 'utils/chat/chatConstants';
import { preserveMarkdownLineBreaks } from 'utils/chat/chatHelpers';

const AssistantMessageMarkdown = ({ fallbackText, isStreaming, message, messageIndex }) => {
    if (messageIndex < 0) {
        return (
            <Text color={'inherit'} sx={{ whiteSpace: 'pre-wrap' }}>
                {fallbackText}
            </Text>
        );
    }

    return (
        <MessageProvider index={messageIndex} isLast={isStreaming} message={message}>
            <MessagePrimitive.Parts>
                {({ part }) => {
                    if (part.type !== 'text') return null;

                    return (
                        <StyledMarkdownTextPrimitive
                            components={{ a: ChatMarkdownLink }}
                            preprocess={preserveMarkdownLineBreaks}
                            remarkPlugins={[remarkGfm]}
                            smooth={isStreaming}
                        />
                    );
                }}
            </MessagePrimitive.Parts>
        </MessageProvider>
    );
};

const ChatMessages = ({ hasLoadingPlaceholder, isRunning, messages }) => {
    const aui = useAui();
    const { t } = useTranslation('assignment');
    const { contentRef, copyAssistantMessage, timeline } = useChatMessagesViewModel({
        isRunning,
        messages,
    });

    const renderSystemMessage = (item) => (
        <SystemMessageRow key={item.id} alignItems={'center'} direction={'row'} spacing={1}>
            {typeof item.draftNumber === 'number' ? (
                <Text color={'light'} component={'span'} variant={'p3'}>
                    <Trans
                        i18nKey={'assignment:chat.submission-created-rich-with-time'}
                        defaults={'<draft/> has been submitted at {{time}}'}
                        values={{ time: item.timeLabel }}
                        components={{
                            draft: <ChatDraftTag component={'span'} type={'draft'} value={item.draftNumber} />,
                        }}
                    />
                </Text>
            ) : (
                <Text color={'light'} variant={'p3'}>
                    {item.text}
                </Text>
            )}
        </SystemMessageRow>
    );

    const renderMessageMeta = (item) => {
        if (!item.shouldShowMessageMeta) return null;

        return (
            <MessageMetaRow role={item.role} direction={'row'} spacing={0.5}>
                <MessageTimestamp color={'light'} role={item.role} variant={'p3'}>
                    {item.timeLabel}
                </MessageTimestamp>
                {item.role === ChatRole.ASSISTANT && (
                    <CopyButton
                        aria-label={'Copy assistant response'}
                        kind={'copy'}
                        size={'small'}
                        variant={'square'}
                        onClick={() =>
                            copyAssistantMessage({
                                fallbackText: item.text,
                                interactionResponse: item.interactionResponse,
                                messageId: item.id,
                                requestedInteraction: item.requestedInteraction,
                            })
                        }
                    />
                )}
            </MessageMetaRow>
        );
    };

    const sendInteractionResponse = ({ item, messageText, response }) => {
        if (isRunning || item.interactionResponse) return;

        aui.thread().append({
            role: 'user',
            content: [{ type: 'text', text: messageText }],
            metadata: {
                custom: {
                    interaction_response: {
                        source_message_id: item.id,
                        requested_interaction_id: item.requestedInteraction.id,
                        ...response,
                    },
                },
            },
        });
    };

    const renderRequestedInteraction = (item) => {
        if (!item.requestedInteraction || item.isStreamingAssistantMessage) return null;

        return (
            <ChatPromptDispatcher
                disabled={isRunning || Boolean(item.interactionResponse)}
                prompt={item.requestedInteraction}
                response={item.interactionResponse}
                onSubmit={({ messageText, response }) => sendInteractionResponse({ item, messageText, response })}
            />
        );
    };

    const renderChatMessage = (item) => (
        <MessageRow key={item.id} role={item.role} spacing={1}>
            <MessageCopyContent data-message-copy-id={item.id}>
                <MessageBubble role={item.role}>
                    {item.role === ChatRole.ASSISTANT ? (
                        <AssistantMessageContent data-message-content-id={item.id}>
                            <AssistantMessageMarkdown
                                fallbackText={item.text}
                                isStreaming={item.isStreamingAssistantMessage}
                                message={item.message}
                                messageIndex={item.messageIndex}
                            />
                        </AssistantMessageContent>
                    ) : (
                        <Text color={'inherit'} sx={{ whiteSpace: 'pre-wrap' }}>
                            {item.text}
                        </Text>
                    )}
                </MessageBubble>
                {item.role === ChatRole.ASSISTANT ? renderRequestedInteraction(item) : null}
            </MessageCopyContent>
            {renderMessageMeta(item)}
        </MessageRow>
    );

    const renderLoadingState = () => {
        if (!hasLoadingPlaceholder) return null;

        return (
            <LoadingMessageRow spacing={1}>
                <LoadingMessageText variant={'p3'}>{t('chat.thinking', 'Thinking...')}</LoadingMessageText>
            </LoadingMessageRow>
        );
    };

    return (
        <MessagesStack ref={contentRef} spacing={5}>
            {timeline.map((group) => (
                <Stack key={group.dateKey} spacing={5}>
                    <DateDivider>
                        <Text color={'light'} variant={'p3'}>
                            {t('common:ui.format-date-long', { val: group.date })}
                        </Text>
                    </DateDivider>

                    {group.items.map((item) => (
                        <Stack key={item.id} spacing={5}>
                            {item.role === ChatRole.SYSTEM ? renderSystemMessage(item) : renderChatMessage(item)}
                        </Stack>
                    ))}
                </Stack>
            ))}

            {renderLoadingState()}
        </MessagesStack>
    );
};

const StyledMarkdownTextPrimitive = styled(MarkdownTextPrimitive)(({ theme }) => getChatMarkdownStyles({ theme }));

const MessagesStack = styled(Stack)(({ theme }) => {
    return {
        minHeight: 0,
        flexShrink: 0,

        '& > *:first-child': {
            paddingTop: theme.spacing(4),
        },
        '& > *:last-child': {
            paddingBottom: '16px',
        },
    };
});

const DateDivider = styled(Stack)(() => {
    return {
        alignItems: 'center',
        textAlign: 'center',
    };
});

const SystemMessageRow = styled(Stack)(({ theme }) => {
    return {
        paddingBottom: theme.spacing(1),
    };
});

const ChatDraftTag = styled(Tag)(({ theme }) => {
    return {
        background: theme.palette.ensightful.primary[50],
        color: theme.palette.ensightful.neutral[800],
        height: 'auto',
    };
});

const MessageRow = styled(Stack, {
    shouldForwardProp: (prop) => prop !== 'role',
})(({ role }) => {
    return {
        alignItems: role === ChatRole.USER ? 'flex-end' : 'flex-start',
    };
});

const MessageBubble = styled(Stack, {
    shouldForwardProp: (prop) => prop !== 'role',
})(({ theme, role }) => {
    return {
        maxWidth: role === ChatRole.USER ? '85%' : '90%',
        lineHeight: '20px',
        borderRadius: role === ChatRole.USER ? '16px' : 0,
        padding: role === ChatRole.USER ? theme.spacing(2, 4) : 0,
        background: role === ChatRole.USER ? theme.palette.ensightful.neutral[100] : 'transparent',
        color: theme.palette.ensightful.neutral[900],
    };
});

const AssistantMessageContent = styled('div')(() => {
    return {
        width: '100%',
    };
});

const MessageCopyContent = styled('div')(() => {
    return {
        display: 'contents',
    };
});

const MessageMetaRow = styled(Stack, {
    shouldForwardProp: (prop) => prop !== 'role',
})(({ role }) => {
    return {
        alignItems: 'center',
        justifyContent: role === ChatRole.USER ? 'flex-end' : 'flex-start',
    };
});

const MessageTimestamp = styled(Text, {
    shouldForwardProp: (prop) => prop !== 'role',
})(({ role }) => {
    return {
        textAlign: role === ChatRole.USER ? 'right' : 'left',
    };
});

const CopyButton = styled(IconButton)(() => {
    return {
        minWidth: 0,
    };
});

const LoadingMessageRow = styled(Stack)(() => {
    return {
        alignItems: 'flex-start',
    };
});

const LoadingMessageText = styled(Text)(({ theme }) => {
    return {
        background: `linear-gradient(90deg, 
            ${theme.palette.ensightful.neutral[800]} 0%, 
            ${theme.palette.ensightful.neutral[400]} 50%, 
            ${theme.palette.ensightful.neutral[800]} 100%
        )`,
        backgroundSize: '180px 100%',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        color: 'transparent',
        WebkitTextFillColor: 'transparent',
        animation: 'chat-loading-gradient 2s linear infinite',

        '@keyframes chat-loading-gradient': {
            '0%': {
                backgroundPosition: '0px center',
            },
            '100%': {
                backgroundPosition: '180px center',
            },
        },
    };
});

export default ChatMessages;
